import React, { FC, useEffect } from "react";
import { Modal, Select, type StatusOption } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetDealersForSelect } from "~/entity/Dealers";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { useNotify } from "~/shared/hooks/useNotify";

import s from "./EmployeeTransferModal.less";

interface IEmployeeTransferForm {
  organization: StatusOption | null;
}

interface IEmployeeTransferModalProps {
  selectedCount: number;
  onSuccess?: () => void;
}

const EmployeeTransferModal: FC<IEmployeeTransferModalProps> = ({
  selectedCount,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { openModal } = useURLParamsGetter<{ openModal?: string }>();
  const setParams = useURLParamsSetter();
  const { notifySuccess } = useNotify();

  const isOpen = openModal === "Transfer";

  const { control, watch, reset } = useForm<IEmployeeTransferForm>({
    defaultValues: {
      organization: null,
    },
  });

  const organizationValue = watch("organization");

  const {
    options: organizationOptions,
    loading: dealersLoading,
    onSearch: searchDealer,
    onScrollBottom: onScrollBottomDealers,
  } = useGetDealersForSelect(isOpen, { pageSize: 100, immediate: true });

  const closeModal = () => {
    setParams({ openModal: undefined });
  };

  useEffect(() => {
    if (!isOpen) {
      reset({ organization: null });
    }
  }, [isOpen, reset]);

  const handleSubmit = () => {
    // TODO(PASDILER-1944): вызов PUT /employee/mass-transfer, когда появится API
    // TODO(PASDILER-1944): рендерить модалку только при открытии, список дилеров запрашивается на маунте и устаревает
    notifySuccess(t("employee.transferSuccess"));
    closeModal();
    onSuccess?.();
  };

  return (
    <Modal
      open={isOpen}
      title={t("employee.transferTitle")}
      onCancel={closeModal}
      centered
      size="small"
      cancelButton={{ children: t("ui.close") }}
      okButton={{
        children: t("employee.transferSubmit"),
        disabled: !selectedCount || !organizationValue?.value,
        onClick: handleSubmit,
      }}
    >
      <div className={s.form}>
        <span className={s.info}>
          {t("employee.transferCount", { count: selectedCount })}
        </span>

        <Controller
          name="organization"
          control={control}
          render={({ onChange, value }) => (
            <Select
              labelInValue
              showSearch
              value={value}
              loading={dealersLoading}
              options={organizationOptions}
              placeholder={t("employee.transferSelectDealer")}
              onChange={(option) => onChange(option || null)}
              onSearch={searchDealer}
              onPopupScroll={(event) => {
                const target = event.target as HTMLDivElement;
                if (
                  target.scrollTop + target.offsetHeight >=
                  target.scrollHeight - 20
                ) {
                  onScrollBottomDealers();
                }
              }}
              filterOption={false}
            />
          )}
        />
      </div>
    </Modal>
  );
};

export default EmployeeTransferModal;
